import apiClient, { API_BASE_URL } from "./apiClient.js";

export const BASE_URL = API_BASE_URL;

// Export apiClient as api to ensure all API calls benefit from the 401 mutex refresh queue
export const api = apiClient;

export async function saveClinicalAssessment(visitId, data, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to save clinical assessment.");
  }

  let url = `/visit/${visitId}/clinical/assessment`;

  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const isFormData =
    (typeof FormData !== "undefined" && data instanceof FormData) ||
    data?.constructor?.name === "FormData";

  // When sending FormData, explicitly unset Content-Type so Axios/browser computes
  // multipart/form-data with the correct boundary parameter.
  const config = isFormData ? { headers: { "Content-Type": undefined } } : {};

  const response = await api.post(url, data, config);
  return response.data;
}

export async function getClinicalAssessment(visitId, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to get clinical assessment.");
  }

  let url = `/visit/${visitId}/clinical/assessment`;

  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.get(url);
  return response.data;
}

export function getClinicalAssessmentDocumentUrl(visitId, docIndex, patientId) {
  if (!visitId || docIndex === undefined || docIndex === null) return "";
  let url = `${BASE_URL}/visit/${visitId}/clinical/assessment/document/${docIndex}`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }
  return url;
}

export async function viewClinicalAssessmentDocument(visitId, docIndex, patientId) {
  if (!visitId || docIndex === undefined || docIndex === null) {
    throw new Error("visitId and docIndex are required to view assessment document.");
  }

  let url = `/visit/${visitId}/clinical/assessment/document/${docIndex}`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.get(url, { responseType: "blob" });
  const mimeType = response.headers?.["content-type"] || "application/pdf";
  const blob = new Blob([response.data], { type: mimeType });
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, "_blank");
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  return true;
}

export async function downloadClinicalAssessmentDocument(visitId, docIndex, patientId, fileName = "document") {
  if (!visitId || docIndex === undefined || docIndex === null) {
    throw new Error("visitId and docIndex are required to download assessment document.");
  }

  let url = `/visit/${visitId}/clinical/assessment/document/${docIndex}`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.get(url, { responseType: "blob" });
  const mimeType = response.headers?.["content-type"] || "application/octet-stream";
  const blob = new Blob([response.data], { type: mimeType });
  const blobUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
  return true;
}

export async function deleteClinicalAssessmentDocument(visitId, docIndex, patientId) {
  if (!visitId || docIndex === undefined || docIndex === null) {
    throw new Error("visitId and docIndex are required to delete assessment document.");
  }

  let url = `/visit/${visitId}/clinical/assessment/document/${docIndex}`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.delete(url);
  return response.data;
}

export async function saveClinicalSoapNotes(visitId, body, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to save SOAP notes.");
  }

  let url = `/visit/${visitId}/clinical/soap-notes`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.post(url, body);
  return response.data;
}

export async function getClinicalSoapNotes(visitId, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to get SOAP notes.");
  }

  let url = `/visit/${visitId}/clinical/soap-notes`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.get(url);
  return response.data;
}

export async function saveClinicalPrescription(visitId, body, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to save prescription.");
  }

  let url = `/visit/${visitId}/clinical/prescription`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.post(url, body);
  return response.data;
}

export async function getClinicalPrescription(visitId, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to get prescription.");
  }

  let url = `/visit/${visitId}/clinical/prescription`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.get(url);
  return response.data;
}

export async function searchMedicines(query, count = 10) {
  const response = await api.get(`/terminology/medicines`, {
    params: { q: query, count },
  });
  return response.data;
}

export async function saveClinicalLabResults(visitId, body, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to save lab results.");
  }

  let url = `/visit/${visitId}/clinical/lab-results`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.post(url, body);
  return response.data;
}

export async function getClinicalLabResults(visitId, patientId) {
  if (!visitId) {
    throw new Error("visitId is required to get lab results.");
  }

  let url = `/visit/${visitId}/clinical/lab-results`;
  if (patientId) {
    url += `?patientId=${encodeURIComponent(patientId)}`;
  }

  const response = await api.get(url);
  return response.data;
}

export async function getAllPatients(params = {}) {
  const response = await api.get("/patients/all", { params });
  return response.data;
}

export async function getPatientsAll(params = {}) {
  const queryParams = {
    page: params.page || 1,
    limit: params.limit || 50,
  };
  if (params.tab && params.tab.toLowerCase() !== "all") {
    queryParams.tab = params.tab.toLowerCase();
  }
  if (params.search && params.search.trim()) {
    queryParams.search = params.search.trim();
  }
  const response = await api.get("/patients/all", { params: queryParams });
  return response.data;
}

export async function getPatientById(patientIdOrUhid) {
  if (!patientIdOrUhid) {
    throw new Error("patientIdOrUhid is required to fetch patient details.");
  }
  const response = await api.get(`/patient/${encodeURIComponent(patientIdOrUhid)}`);
  const data = response.data?.data !== undefined ? response.data.data : response.data;
  if (data && typeof data === "object") {
    return {
      ...data,
      id: data._id || data.id,
      fullName: data.name || data.fullName,
      maskedAadhaar: data.maskedAadhaar || data.aadhaarNumber || data.aadhaar_masked || "",
      maskedAbha: data.maskedAbha || data.formattedAbhaId || (data.abhaNumber ? `**** ${String(data.abhaNumber).slice(-4)}` : ""),
      abhaAddress: data.abhaAddress || data.ABHAAddress || data.abhaaddress || "",
      isAbhaLinked: Boolean(data.isAbhaLinked),
    };
  }
  return data;
}

export async function searchPatients(query = "", page = 1, limit = 10) {
  const url = `/patient/search?query=${encodeURIComponent(query)}&page=${page}&limit=${limit}`;
  const response = await api.get(url);
  return response.data;
}

export async function getVisits(
  search = "",
  status = "",
  page = 1,
  limit = 10,
) {
  let url = `/opd/visits?search=${encodeURIComponent(search)}&page=${page}&limit=${limit}`;
  if (status && status !== "All") {
    url += `&status=${encodeURIComponent(status)}`;
  }
  const response = await api.get(url);
  return response.data;
}

export async function addVisit(patientId, body = {}) {
  const url = `/patient/${encodeURIComponent(patientId)}/visit`;
  const response = await api.post(url, {
    visitType: body.visitType ?? "",
    description: body.description ?? "",
    departmentId: body.departmentId ?? "",
    doctorId: body.doctorId ?? body.consultingDoctorId ?? "",
    counterId: body.counterId || 1,
  });
  return response.data?.data ?? response.data;
}

export async function getDepartments() {
  const response = await api.get("/departments");
  return response.data;
}

export async function getDoctors(departmentId = "") {
  let url = "/doctors";
  if (departmentId) {
    url += `?department_id=${encodeURIComponent(departmentId)}`;
  }
  const response = await api.get(url);
  return response.data;
}

export async function completeRegistration(id, body) {
  const url = `/opd/complete-registration/${encodeURIComponent(id)}`;
  const payload = {
    ...body,
    counterId: body.counterId || 1,
  };
  const response = await api.post(url, payload);
  return response.data;
}

// Billing: POST /api/billing – create (links to visit)
export async function createBilling(payload) {
  const response = await api.post("/billing", payload);
  return response.data;
}

// GET /api/billing/visit/:visitId – get by visit id
export async function getBilling(visitId) {
  const response = await api.get(`/billing/visit/${visitId}`);
  return response.data;
}

// PUT /api/billing/:id – update billings/totalAmount/status
export async function updateBilling(id, payload) {
  const response = await api.put(`/billing/${id}`, payload);
  return response.data;
}

// Discharge summary: POST /visit/:visitId/clinical/discharge-summary
export async function saveClinicalDischargeSummary(visitId, body, patientId) {
  if (!visitId)
    throw new Error("visitId is required to save discharge summary.");
  let url = `/visit/${visitId}/clinical/discharge-summary`;
  if (patientId) url += `?patientId=${encodeURIComponent(patientId)}`;
  const response = await api.post(url, body);
  return response.data;
}

// GET /visit/:visitId/clinical/discharge-summary
export async function getClinicalDischargeSummary(visitId, patientId) {
  if (!visitId)
    throw new Error("visitId is required to get discharge summary.");
  let url = `/visit/${visitId}/clinical/discharge-summary`;
  if (patientId) url += `?patientId=${encodeURIComponent(patientId)}`;
  const response = await api.get(url);
  return response.data;
}
// GET /api/v3/hiu/patient/:patientId/external-records
export async function getExternalRecords(patientId, params = {}) {
  if (!patientId) {
    throw new Error("patientId is required to get external records.");
  }
  const queryString = new URLSearchParams(params).toString();
  const url = `/hiu/patient/${encodeURIComponent(patientId)}/external-records?${queryString}`;
  const response = await api.get(url);
  return response.data;
}

// GET /api/v3/hiu/external-records/:id
export async function getExternalRecordById(recordId) {
  if (!recordId) {
    throw new Error("recordId is required to get external record details.");
  }
  const url = `/hiu/external-records/${recordId}`;
  const response = await api.get(url);
  return response.data;
}

// POST /api/v3/patient/check-existing
export async function checkExistingPatient(params = {}) {
  const response = await api.post("/patient/check-existing", params);
  return response.data;
}

// PATCH /api/v3/patient/:id/update-and-visit
export async function updatePatientAndVisit(patientId, data) {
  if (!patientId) {
    throw new Error("patientId is required for update-and-visit.");
  }
  const response = await api.patch(
    `/patient/${encodeURIComponent(patientId)}/update-and-visit`,
    data,
  );
  return response.data;
}
// PATCH /api/v3/patient/:id
export async function updatePatientProfile(patientId, data) {
  if (!patientId) {
    throw new Error("patientId is required for patient profile update.");
  }
  const response = await api.patch(
    `/patient/${encodeURIComponent(patientId)}`,
    data,
  );
  return response.data;
}

// ==== Lab Report Module Endpoints ====

// GET /api/lab-tests/types
export async function getAvailableTestTypes() {
  const response = await api.get("/lab-tests/types");
  return response.data;
}

// GET /api/lab-tests/parameters/:testType
export async function getLabTestParameters(testType) {
  const response = await api.get(
    `/lab-tests/parameters/${encodeURIComponent(testType)}`,
  );
  return response.data;
}

// POST /api/lab-reports/upsert — save or update a test entry within the centralized record
export async function upsertLabReport(data) {
  const response = await api.post("/lab-reports/upsert", data);
  return response.data;
}

// GET /api/lab-reports/visit/:visitId — prefill form for a visit
export async function getLabReportByVisit(visitId) {
  const response = await api.get(
    `/lab-reports/visit/${encodeURIComponent(visitId)}`,
  );
  return response.data;
}

// GET /api/lab-reports/:id
export async function getLabReportById(id) {
  const response = await api.get(`/lab-reports/${encodeURIComponent(id)}`);
  return response.data;
}

// PUT /api/lab-reports/:id/test/:testType — update a specific test within the record
export async function updateLabReportTest(id, testType, data) {
  const response = await api.put(
    `/lab-reports/${encodeURIComponent(id)}/test/${encodeURIComponent(testType)}`,
    data,
  );
  return response.data;
}

// GET /api/lab-reports/patient/:patientId — each item has tests[] array
export async function getPatientLabReports(patientId, params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const url = `/lab-reports/patient/${encodeURIComponent(patientId)}${queryString ? `?${queryString}` : ""}`;
  const response = await api.get(url);
  return response.data;
}

// PATCH /api/lab-reports/:id/finalize — finalize all tests or a specific testType
export async function finalizeLabReport(id, testType) {
  const body = testType ? { testType } : undefined;
  const response = await api.patch(
    `/lab-reports/${encodeURIComponent(id)}/finalize`,
    body,
  );
  return response.data;
}

// ── Pharmacy Dispensing Endpoints ──

// GET /api/pharmacy/queue
export async function getPharmacyQueue(params = {}) {
  const queryParams = new URLSearchParams();
  if (params.status && params.status !== "ALL") {
    queryParams.append("status", params.status);
  }
  if (params.department && params.department !== "All" && params.department !== "All Departments") {
    queryParams.append("department", params.department);
  }
  if (params.search && params.search.trim()) {
    queryParams.append("search", params.search.trim());
  }
  if (params.date) queryParams.append("date", params.date);
  if (params.page) queryParams.append("page", String(params.page));
  if (params.limit) queryParams.append("limit", String(params.limit));

  const qs = queryParams.toString();
  const url = `/pharmacy/queue${qs ? `?${qs}` : ""}`;
  const response = await api.get(url);
  return response.data;
}

// POST /api/pharmacy/dispense
export async function submitPharmacyDispense(payload) {
  const response = await api.post("/pharmacy/dispense", payload);
  return response.data;
}

// GET /api/pharmacy/dispense/by-visit/:visitId
export async function getPharmacyDispenseRecord(visitId) {
  if (!visitId) throw new Error("visitId is required.");
  const response = await api.get(`/pharmacy/dispense/by-visit/${encodeURIComponent(visitId)}`);
  return response.data;
}

